import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Save, Eye, FileText, Star } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn, Badge } from "../components/common";
import { Modal, Field, Selecter, braceIssues, BracesWarning } from "./crm/crmui";

const PLATAFORMAS = ["", "Cognietica", "Privaxia", "Assiduo", "Recrutai", "Outra"];

const EMPTY = { nome: "", plataforma: "", versao: "v1", corpo: "", ativo: false, variaveis: [], tags: [] };

const STARTER = `# Contrato de prestacao de servicos SaaS

**Numero:** {{numero_contrato}}
**Data:** {{hoje}}

## Partes

Entre **Cogniseg** e **{{cliente_nome}}** (NIF {{cliente_nif}}), com sede em {{cliente_morada}}.

## Servico

A Cogniseg disponibiliza acesso a plataforma **{{plataforma}}** conforme condicoes abaixo:

- Valor mensal (MRR): {{mrr}} {{moeda}}
- Valor total: {{valor_total}} {{moeda}}
- Duracao: {{duracao_meses}} meses
- Inicio: {{data_inicio}}
- Fim previsto: {{data_fim}}

## Pagamento

Condicoes: {{condicoes_pagamento}}
IBAN: {{iban}}

## Assinaturas

Cliente: ________________________     Cogniseg: ________________________
`;

export default function ContractTemplates() {
  const [rows, setRows] = useState([]);
  const [filtroPlat, setFiltroPlat] = useState("");
  const [edit, setEdit] = useState(null);
  const [standardVars, setStandardVars] = useState([]);
  const [preview, setPreview] = useState({ open: false, corpo: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = filtroPlat ? { plataforma: filtroPlat } : {};
      const [{ data }, v] = await Promise.all([
        api.get("/cpq/contract-templates", { params }),
        api.get("/cpq/contract-templates/vars"),
      ]);
      setRows(data || []);
      setStandardVars(v.data.standard || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [filtroPlat]); // eslint-disable-line

  const salvar = async () => {
    if (!edit.nome.trim()) return toast.error("Indique o nome do template.");
    setBusy(true);
    try {
      if (edit.id) {
        await api.put(`/cpq/contract-templates/${edit.id}`, edit);
        toast.success("Template atualizado.");
      } else {
        await api.post("/cpq/contract-templates", edit);
        toast.success("Template criado.");
      }
      setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remover = async (t) => {
    if (!confirm(`Eliminar template "${t.nome}"?`)) return;
    try {
      await api.delete(`/cpq/contract-templates/${t.id}`);
      toast.success("Eliminado."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const duplicar = async (t) => {
    try {
      await api.post(`/cpq/contract-templates/${t.id}/duplicate`);
      toast.success("Template duplicado."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doPreview = async () => {
    try {
      const { data } = await api.post("/cpq/contract-templates/render", { corpo: edit.corpo });
      setPreview({ open: true, corpo: data.corpo });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const insertVar = (v) => {
    setEdit((e) => ({ ...e, corpo: (e.corpo || "") + `{{${v}}}` }));
  };

  const grouped = useMemo(() => {
    const map = {};
    (rows || []).forEach((r) => {
      const k = r.plataforma || "generico";
      (map[k] = map[k] || []).push(r);
    });
    return map;
  }, [rows]);

  return (
    <>
      <PageHeader
        overline="Contratos"
        title="Templates de contratos"
        action={
          <Btn onClick={() => setEdit({ ...EMPTY, corpo: STARTER })} testid="new-template-btn">
            <Plus size={16} /> Novo template
          </Btn>
        }
      />

      <div className="flex items-center gap-2 mb-4">
        <label className="text-xs text-muted2">Filtrar por plataforma:</label>
        <select
          value={filtroPlat}
          onChange={(e) => setFiltroPlat(e.target.value)}
          data-testid="template-filter-plat"
          className="text-xs border border-tint rounded-md px-2 py-1"
        >
          <option value="">Todas</option>
          {PLATAFORMAS.filter(Boolean).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {Object.keys(grouped).length === 0 && (
        <div className="text-center text-muted2 border border-tint rounded-lg py-12" data-testid="empty-templates">
          Sem templates de contrato. Crie o primeiro.
        </div>
      )}

      {Object.entries(grouped).map(([plat, items]) => (
        <div key={plat} className="mb-6" data-testid={`template-group-${plat}`}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-navy mb-2">
            {plat === "generico" ? "Genericos (todas as plataformas)" : plat}
          </div>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {items.map((t) => (
              <div key={t.id} data-testid={`template-${t.id}`} className="border border-tint rounded-lg p-3 bg-white hover:shadow-sm transition">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-navy text-sm truncate flex items-center gap-1.5">
                      <FileText size={13} /> {t.nome}
                    </div>
                    <div className="text-[10px] text-muted2 mt-0.5 flex items-center gap-2">
                      <span>{t.versao}</span>
                      {t.ativo && <span className="inline-flex items-center gap-1 text-amber-700"><Star size={9} /> ativo</span>}
                    </div>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 mt-2 line-clamp-2">
                  {(t.corpo || "").replace(/[#*_>-]/g, "").slice(0, 120) || "-"}
                </div>
                <div className="flex items-center gap-1 mt-3">
                  <button onClick={() => setEdit({ ...t })} data-testid={`template-edit-${t.id}`}
                    className="text-[11px] px-2 py-1 rounded bg-blue text-white hover:bg-[var(--blue-hover)]">Editar</button>
                  <button onClick={() => duplicar(t)} data-testid={`template-duplicate-${t.id}`}
                    className="text-[11px] px-2 py-1 rounded border border-tint text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"><Copy size={11} />Duplicar</button>
                  <button onClick={() => remover(t)} data-testid={`template-delete-${t.id}`}
                    className="text-[11px] px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"><Trash2 size={11} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Editar template" : "Novo template"} testid="template-modal" size="xl">
        {edit && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Nome" value={edit.nome} onChange={(v) => setEdit({ ...edit, nome: v })} testid="template-nome" />
              <Selecter label="Plataforma" value={edit.plataforma} onChange={(v) => setEdit({ ...edit, plataforma: v })}
                options={[{ value: "", label: "- Generico -" }, ...PLATAFORMAS.filter(Boolean)]} testid="template-plataforma" />
              <Field label="Versao" value={edit.versao} onChange={(v) => setEdit({ ...edit, versao: v })} testid="template-versao" />
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!edit.ativo} onChange={(e) => setEdit({ ...edit, ativo: e.target.checked })} data-testid="template-ativo" />
              Marcar como template ativo para esta plataforma
            </label>

            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Corpo (Markdown, use variaveis {'{{chave}}'})</label>
              <textarea
                value={edit.corpo}
                onChange={(e) => setEdit({ ...edit, corpo: e.target.value })}
                data-testid="template-corpo"
                rows={16}
                className="w-full px-3 py-2 border border-tint rounded-md text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue/20"
              />
              <BracesWarning issue={braceIssues(edit.corpo, standardVars)} testid="template-corpo-warn" />
            </div>

            <div className="bg-slate-50 border border-tint rounded-md p-2">
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Variaveis padrao (clique para inserir)</div>
              <div className="flex flex-wrap gap-1">
                {standardVars.map((v) => (
                  <button key={v} type="button" onClick={() => insertVar(v)}
                    data-testid={`insert-var-${v}`}
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white border border-tint hover:border-blue hover:text-blue">
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white -mx-6 px-6 py-3 border-t border-tint">
              <Btn variant="ghost" onClick={() => setEdit(null)}>Cancelar</Btn>
              <Btn variant="ghost" onClick={doPreview} testid="template-preview-btn"><Eye size={14} /> Pre-visualizar</Btn>
              <Btn onClick={salvar} testid="template-save-btn" disabled={busy}><Save size={14} /> {busy ? "..." : "Guardar"}</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={preview.open} onClose={() => setPreview({ open: false, corpo: "" })} title="Pre-visualizacao" testid="template-preview-modal" size="lg">
        <pre className="text-xs whitespace-pre-wrap font-mono bg-slate-50 border border-tint rounded-md p-3 max-h-[60vh] overflow-y-auto">
{preview.corpo}
        </pre>
      </Modal>
    </>
  );
}
